// Helper to find usages of a location reference across scenes and shots
export function findUsagesForKey(key, scenes = []) {
  const placeholder = `__${key}__`
  const usages = []

  const makeSnippet = (text, match) => {
    if (!text) return ''
    const t = String(text)
    const idx = t.indexOf(match)
    if (idx === -1) return t.slice(0, 200)
    const start = Math.max(0, idx - 40)
    const end = Math.min(t.length, idx + match.length + 120)
    return (
      (start > 0 ? '... ' : '') +
      t.slice(start, end).trim() +
      (end < t.length ? ' ...' : '')
    )
  }

  scenes.forEach((scene, sceneIndex) => {
    const sceneTitle = scene.title || `Scene ${sceneIndex + 1}`

    // Check scene description
    if (scene.description && String(scene.description).includes(key)) {
      usages.push({
        type: 'scene',
        sceneTitle,
        shotTitle: null,
        matchText: makeSnippet(scene.description, key)
      })
    } else if (
      scene.description &&
      String(scene.description).includes(placeholder)
    ) {
      usages.push({
        type: 'scene',
        sceneTitle,
        shotTitle: null,
        matchText: makeSnippet(scene.description, placeholder)
      })
    }

    // Check shots inside the scene
    if (Array.isArray(scene.shots)) {
      scene.shots.forEach((shot, shotIndex) => {
        const shotLabel = shot.title || `Shot ${shotIndex + 1}`

        const fieldsToCheck = [
          { name: 'title', value: shot.title },
          { name: 'initial_image_prompt', value: shot.initial_image_prompt },
          { name: 'video_prompt', value: shot.video_prompt }
        ]

        let matched = null
        for (const f of fieldsToCheck) {
          if (f.value && String(f.value).includes(key)) {
            matched = { field: f.name, text: makeSnippet(f.value, key) }
            break
          }
          if (f.value && String(f.value).includes(placeholder)) {
            matched = { field: f.name, text: makeSnippet(f.value, placeholder) }
            break
          }
        }

        // Check dialogue lines
        let dialogueMatches = []
        if (!matched && shot.dialogue) {
          const entries = Object.entries(shot.dialogue || {})
          for (const [character, v] of entries) {
            const text = typeof v === 'object' ? v.text : v
            if (text && String(text).includes(key)) {
              dialogueMatches.push({ character, text: makeSnippet(text, key) })
            } else if (text && String(text).includes(placeholder)) {
              dialogueMatches.push({
                character,
                text: makeSnippet(text, placeholder)
              })
            }
          }
        }

        if (matched || dialogueMatches.length) {
          usages.push({
            type: 'shot',
            sceneTitle,
            shotTitle: shotLabel,
            matchedField: matched ? matched.field : null,
            matchedText: matched ? matched.text : null,
            dialogueMatches
          })
        }
      })
    }
  })

  // Deduplicate usages by scene+shot+matchedText
  const seen = new Set()
  return usages.filter(u => {
    const id = `${u.sceneTitle}::${u.shotTitle || ''}::${u.matchedText ||
      u.matchText ||
      JSON.stringify(u.dialogueMatches)}`
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

export const getBaseLocations = (keys = []) => {
  const bases = new Set()
  const regex = /__location(\d+)\.(\d+)__/

  keys.forEach(k => {
    const match = k.match(regex)
    if (match) {
      bases.add(parseInt(match[1], 10))
    }
  })
  return Array.from(bases).sort((a, b) => a - b)
}

export const calculateNextKey = (type, base, keys = []) => {
  const regex = /__location(\d+)\.(\d+)__/

  let maxBase = 0
  let maxSub = 0

  if (type === 'new') {
    keys.forEach(k => {
      const match = k.match(regex)
      if (match) {
        const b = parseInt(match[1], 10)
        if (b > maxBase) maxBase = b
      }
    })
    return `__location${maxBase + 1}.1__`
  } else if (type === 'existing' && base) {
    keys.forEach(k => {
      const match = k.match(regex)
      if (match) {
        const b = parseInt(match[1], 10)
        const s = parseInt(match[2], 10)
        if (b === base && s > maxSub) maxSub = s
      }
    })
    return `__location${base}.${maxSub + 1}__`
  }
  return ''
}

export const validateLocationKey = key => {
  // Check if it matches the format __locationX.Y__
  const regex = /^__location\d+\.\d+__$/
  return regex.test(key)
}
